import { PRE_CHAT_FORM_DISPLAY_RULE_LABELS } from '~/features/preChatForms/constants'
import {
  EFormDisplayRule,
  EFormFieldMapping,
  EFormStatus,
  EPreChatFormFieldKeys,
  EPreChatFormFieldStatus,
  EPreChatFormKeys,
  TPreChatForm,
  TPreChatFormFieldLayoutItem,
  TPreChatFormFieldLayoutSource,
} from '~/features/preChatForms/models'
import { EServerPath } from '~/models/api'

export const queryKeys = {
  preChatForms: (orgId: string) => [EServerPath.forms, orgId] as const,
  preChatForm: (formId: string) => [EServerPath.form, formId] as const,
}

export const getPreChatFormDisplayRuleLabel = (
  displayRule?: EFormDisplayRule,
) => {
  if (!displayRule) {
    return ''
  }

  return PRE_CHAT_FORM_DISPLAY_RULE_LABELS[displayRule]
}

export const getPreChatFormSharedUsageChipLabel = ({
  assignedAgentCount = 0,
  isEditMode = true,
}: {
  assignedAgentCount?: number
  isEditMode?: boolean
}) => {
  if (!isEditMode) {
    return 'Not yet assigned'
  }

  const assistantLabel = assignedAgentCount === 1 ? 'assistant' : 'assistants'

  return `Shared · Used by ${assignedAgentCount} ${assistantLabel}`
}

export const getPreChatFormActiveFields = (form: TPreChatForm) =>
  (form[EPreChatFormKeys.fields] ?? []).filter(
    (field) =>
      field[EPreChatFormFieldKeys.status] !== EPreChatFormFieldStatus.inactive,
  )

export const isPreChatFormActive = (form: TPreChatForm) =>
  form[EPreChatFormKeys.status] === EFormStatus.active

export const sortPreChatFormFields = <T extends TPreChatFormFieldLayoutSource>(
  fields: T[],
): T[] =>
  [...fields].sort(
    (left, right) =>
      left[EPreChatFormFieldKeys.sortOrder] -
      right[EPreChatFormFieldKeys.sortOrder],
  )

export const buildPreChatFormFieldLayout = <
  T extends TPreChatFormFieldLayoutSource,
>(
  sortedFields: T[],
): TPreChatFormFieldLayoutItem<T>[] => {
  const firstNameField = sortedFields.find(
    (field) =>
      field[EPreChatFormFieldKeys.fieldMapping] ===
      EFormFieldMapping.contactFirstName,
  )
  const lastNameField = sortedFields.find(
    (field) =>
      field[EPreChatFormFieldKeys.fieldMapping] ===
      EFormFieldMapping.contactLastName,
  )

  if (!firstNameField || !lastNameField) {
    return sortedFields.map((field) => ({ type: 'field', field }))
  }

  const otherFields = sortedFields.filter(
    (field) => field !== firstNameField && field !== lastNameField,
  )
  const nameRowSortOrder = Math.min(
    firstNameField[EPreChatFormFieldKeys.sortOrder],
    lastNameField[EPreChatFormFieldKeys.sortOrder],
  )
  const layout: TPreChatFormFieldLayoutItem<T>[] = []
  let isNameRowInserted = false

  otherFields.forEach((field) => {
    if (
      !isNameRowInserted &&
      field[EPreChatFormFieldKeys.sortOrder] > nameRowSortOrder
    ) {
      layout.push({
        type: 'nameRow',
        firstName: firstNameField,
        lastName: lastNameField,
      })
      isNameRowInserted = true
    }

    layout.push({ type: 'field', field })
  })

  if (!isNameRowInserted) {
    layout.push({
      type: 'nameRow',
      firstName: firstNameField,
      lastName: lastNameField,
    })
  }

  return layout
}
